import axios from "axios";
import { getAuthorizationToken } from "../config/user";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

/**
 * GET default scheme type list
 * @param {string|Object} [caseTypeOrParams] - optional caseType string, or object { caseType, page, limit, searchKey, name }
 * - name: used for scheme name search (e.g. default-scheme-type?limit=40&name=PMJAY)
 * - searchKey: alias for name, also supported for backward compatibility
 */
function getDefaultSchemeTypes(caseTypeOrParams) {
  const params =
    typeof caseTypeOrParams === "object" && caseTypeOrParams != null
      ? { ...caseTypeOrParams }
      : caseTypeOrParams
        ? { caseType: caseTypeOrParams }
        : {};
  const searchValue =
    params.name != null && String(params.name).trim() !== ""
      ? String(params.name).trim()
      : params.searchKey != null && String(params.searchKey).trim() !== ""
        ? String(params.searchKey).trim()
        : null;
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/default-scheme-type`,
    headers: getAuthorizationToken(),
    params: {
      ...(params.caseType != null &&
        params.caseType !== "" && { caseType: params.caseType }),
      ...(params.page != null && { page: params.page }),
      ...(params.limit != null && { limit: params.limit }),
      ...(searchValue != null && { name: searchValue }),
    },
  });
}

/**
 * POST add scheme type
 * @param {Object} payload - { name: string }
 */
function addSchemeType(payload) {
  return axios({
    method: "post",
    url: `${REACT_APP_API_URL}/add-scheme-type`,
    headers: getAuthorizationToken(),
    data: payload,
  });
}

/**
 * PUT update scheme type
 * @param {Object} payload - { id: number, name: string }
 */
function updateSchemeType(payload) {
  return axios({
    method: "put",
    url: `${REACT_APP_API_URL}/update-scheme-type`,
    headers: getAuthorizationToken(),
    data: payload,
  });
}

/**
 * PUT update scheme–state mapping (activate / deactivate states on a scheme).
 * @param {{ schemeId: number, removeStates?: string[], addStates?: string[] }} payload
 * - removeStates: full state names to mark inactive (e.g. ["Himachal Pradesh"])
 * - addStates: full state names to mark active (e.g. ["Delhi"])
 */
function updateSchemeStateMapping(payload) {
  return axios({
    method: "post",
    url: `${REACT_APP_API_URL}/update-scheme-state-mapping`,
    headers: getAuthorizationToken(),
    data: payload,
  });
}

/**
 * GET scheme procedure cost list
 * @param {Object} params - { schemeId, procedureCostMapping, costMappingId?, searchKey?, procedureSchemeData?, page?, limit? }
 * - costMappingId: state name for STATE mapping (e.g. "Haryana")
 * - searchKey: procedure/speciality name search
 * - procedureSchemeData: "ACTIVE" | "ALL"
 * - page / limit: server pagination (response may include hasMore, total, totalPages)
 */
function getSchemeProcedureCostList({
  schemeId = 2,
  procedureCostMapping = "STATE",
  costMappingId,
  searchKey,
  procedureSchemeData = "ACTIVE",
  page,
  limit,
} = {}) {
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/scheme-procedure-cost-list`,
    headers: getAuthorizationToken(),
    params: {
      schemeId,
      procedureCostMapping,
      ...(procedureSchemeData && { procedureSchemeData }),
      ...(costMappingId != null &&
        String(costMappingId).trim() !== "" && {
          costMappingId: String(costMappingId).trim(),
        }),
      ...(searchKey != null &&
        String(searchKey).trim() !== "" && {
          searchKey: String(searchKey).trim(),
        }),
      ...(page != null && page >= 1 && { page }),
      ...(limit != null && limit >= 1 && { limit }),
    },
  });
}

/**
 * PUT update procedure cost (id is required)
 * @param {Array} payload - e.g. [{ id, procedureCostMapping, stateId, tier1, tier2, tier3 }]
 */
function updateProcedureCost(payload) {
  return axios({
    method: "put",
    url: `${REACT_APP_API_URL}/update-procedure-cost`,
    headers: getAuthorizationToken(),
    data: Array.isArray(payload) ? payload : [payload],
  });
}

/**
 * POST add procedure cost mapping
 * @param {Array} payload - array of cost mapping objects, e.g. [{ schemeId, procedureId, procedureCostMapping, stateId, state, tier1, tier2, tier3, ... }]
 */
function addProcedureCostMapping(payload) {
  return axios({
    method: "post",
    url: `${REACT_APP_API_URL}/add-default-procedure-cost`,
    headers: getAuthorizationToken(),
    data: Array.isArray(payload) ? payload : [payload],
  });
}

/**
 * POST map procedure scheme cost (STATE/NATIONAL/HOSPITAL payloads)
 * @param {Array|Object} payload - array (or single object) as per backend contract
 */
function mapProcedureSchemeCost(payload) {
  return axios({
    method: "post",
    url: `${REACT_APP_API_URL}/map-procedure-scheme-cost`,
    headers: getAuthorizationToken(),
    data: Array.isArray(payload) ? payload : [payload],
  });
}
function getMasterDataList(type) {
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/master-data-list`,
    headers: getAuthorizationToken(),
    params: {
      type, // e.g. "documentType or caseType"
    },
  });
}

/**
 * GET unique speciality list (paginated)
 * @param {Object} [params] - optional { page, limit, searchKey }
 */
function getSpecialityTypes(params = {}) {
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/unique-speciality-list`,
    headers: getAuthorizationToken(),
    params: {
      ...(params.page != null && { page: params.page }),
      ...(params.limit != null && { limit: params.limit }),
      ...(params.searchKey != null &&
        String(params.searchKey).trim() !== "" && {
          searchKey: String(params.searchKey).trim(),
        }),
    },
  });
}

/**
 * POST add or update default speciality (same endpoint for both)
 * @param {Array<{ specialityId?: string, speciality: string }>} payload - e.g. [{ specialityId: "BM1", speciality: "Burns Management1" }]
 */
function addDefaultSpeciality(payload) {
  const data = Array.isArray(payload) ? payload : [payload];
  return axios({
    method: "post",
    url: `${REACT_APP_API_URL}/add-default-speciality`,
    headers: getAuthorizationToken(),
    data,
  });
}

/**
 * Add or update default procedure (including status toggle).
 * Add: { procedureId, procedure, specialityId, speciality, isStratification?, isImplant? }
 * Update status: { id, status: "ACTIVE" | "INACTIVE" }
 */
function addProcedure(payload) {
  const finalPayload = [
    {
      ...payload,
      countryId: "IN",
    },
  ];

  const url = `${REACT_APP_API_URL}/add-default-procedure-v2`;
  return axios.post(url, finalPayload, {
    headers: getAuthorizationToken(),
  });
}



function searchProcedureById(procedureId) {
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/default-procedure-list-v2`,
    headers: getAuthorizationToken(),
    params: {
      procedureId,
    },
  });
}

/**
 * GET default procedure list v2 (paginated) - master procedure list for mapping
 * @param {Object} [params] - { page?, limit?, searchKey? }
 */
function getDefaultProcedureListV2(params = {}) {
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/default-procedure-list-v2`,
    headers: getAuthorizationToken(),
    params: {
      ...(params.page != null && { page: params.page }),
      ...(params.limit != null && { limit: params.limit }),
      ...(params.searchKey != null &&
        String(params.searchKey).trim() !== "" && {
          searchKey: String(params.searchKey).trim(),
        }),
    },
  });
}

function updateDefaultProcedureSpeciality(payload = []) {
  return axios({
    method: "put",
    url: `${REACT_APP_API_URL}/update-default-procedure-speciality`,
    headers: getAuthorizationToken(),
    data: payload, // MUST be an array
  });
}

/**
 * GET procedure list for dropdowns (e.g. cost mapping). Pass limit to get all; no limit may return default page only.
 * @param {Object} [params] - { page?, limit?, procedureSchemeData?, schemeId? }
 */
function getProcedures(params = {}) {
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/scheme-procedure-list`,
    headers: getAuthorizationToken(),
    params: {
      ...(params.page != null && { page: params.page }),
      ...(params.limit != null && { limit: params.limit }),
      ...(params.procedureSchemeData != null && { procedureSchemeData: params.procedureSchemeData }),
      ...(params.schemeId != null && params.schemeId !== "" && { schemeId: params.schemeId }),
    },
  });
}

/**
 * GET scheme-procedure list (paginated) for procedure-scheme mapping table
 * @param {Object} [params] - { page?, limit?, schemeId?, specialityId?, searchKey?, procedureName? }
 */
function getSchemeProcedureList(params = {}) {
  const procedureName =
    params.procedureName != null && params.procedureName.trim() !== ""
      ? params.procedureName.trim()
      : null;
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/scheme-procedure-list`,
    headers: getAuthorizationToken(),
    params: {
      ...(params.page != null && { page: params.page }),
      ...(params.limit != null && { limit: params.limit }),
      ...(params.schemeId != null &&
        params.schemeId !== "" && { schemeId: params.schemeId }),
      ...(params.specialityId != null &&
        params.specialityId !== "" && { specialityId: params.specialityId }),
      ...(params.searchKey != null &&
        params.searchKey.trim() !== "" && {
          searchKey: params.searchKey.trim(),
        }),
      ...(procedureName && { procedureName }),
      ...(params.procedureSchemeData != null && { procedureSchemeData: params.procedureSchemeData }),
    },
  });
}

/**
 * PUT add or update procedure-scheme mapping (same endpoint for both add, update, and inactive)
 * @param {Array<{ schemeId: string|number, procedureId: string, status?: "ACTIVE"|"INACTIVE" }>} payload - e.g. [{ schemeId: "1", procedureId: "MR006D" }] or [{ schemeId: "12", procedureId: "BM009A", status: "INACTIVE" }]
 */
function updateProcedureSchemeMapping(payload) {
  const data = Array.isArray(payload) ? payload : [payload];
  return axios({
    method: "put",
    url: `${REACT_APP_API_URL}/update-procedure-scheme-mapping`,
    headers: getAuthorizationToken(),
    data,
  });
}



function getProcedureslist(searchKey = "") {
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/default-procedure-list`,
    headers: getAuthorizationToken(),
    params: {
      searchKey: searchKey,
    },
  });
}



/**
 * GET default document name list
 * @param {Object} params - { status?: "ACTIVE" | "INACTIVE", searchKey?: string, page?: number, limit?: number }
 */
function getDefaultDocumentNameList(params = {}) {
  const { status = "ACTIVE", searchKey, page, limit } = params;
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/default-document-name-list`,
    headers: getAuthorizationToken(),
    params: {
      status,
      ...(searchKey != null &&
        searchKey.trim() !== "" && { searchKey: searchKey.trim() }),
      ...(page != null && { page }),
      ...(limit != null && { limit }),
    },
  });
}

/**
 * GET default document name list (v2)
 * @param {Object} params - { status?, searchKey?, page?, limit?, documentType? } - documentType filters by e.g. ADMISSIONDOCUMENT, DURINGTREATMENTDOCUMENT, ONDISCHARGEDOCUMENT
 */
function getDefaultDocumentNameListV2(params = {}) {
  const { status = "ACTIVE", searchKey, page, limit, documentType } = params;
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/default-document-name-list-v2`,
    headers: getAuthorizationToken(),
    params: {
      status,
      ...(searchKey != null &&
        searchKey.trim() !== "" && { searchKey: searchKey.trim() }),
      ...(documentType != null &&
        String(documentType).trim() !== "" && {
          documentType: String(documentType).trim(),
        }),
      ...(page != null && { page }),
      ...(limit != null && { limit }),
    },
  });
}

/**
 * GET grouped default document name list (v2)
 * Returns documents grouped by document name, each with array of mappings across document types
 */
function getGroupedDefaultDocumentNameListV2(params = {}) {
  const { searchKey } = params;
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/grouped-default-document-name-list-v2`,
    headers: getAuthorizationToken(),
    params: {
      ...(searchKey != null &&
        searchKey.trim() !== "" && { searchKey: searchKey.trim() }),
    },
  });
}

/**
 * GET document-linked-procedures
 * @param {Object} params - { isDocumentTypeId: boolean, targetId: string|number, page?: number, limit?: number }
 */
function getDocumentLinkedProcedures(params = {}) {
  const { isDocumentTypeId = false, targetId, page, limit } = params;

  const requestParams = {
    isDocumentTypeId,
    targetId,
    ...(page != null && { page }),
    ...(limit != null && { limit }),
  };

  console.log("Request payload:", requestParams);

  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/document-linked-procedures`,
    headers: getAuthorizationToken(),
    params: requestParams,
  });
}


/**
 * PUT add or update default document name
 * Add: payload [{ documentName }]
 * Update: payload [{ id, documentName, status }]
 * @param {Array} payload - Array of one item: { documentName } for add, or { id, documentName, status } for update
 */
function addUpdateDefaultDocName(payload) {
  return axios({
    method: "put",
    url: `${REACT_APP_API_URL}/add-update-default-doc-name`,
    headers: getAuthorizationToken(),
    data: Array.isArray(payload) ? payload : [payload],
  });
}

/**
 * PUT update document type mapping
 * @param {Array} payload - Array of mapping objects, e.g. [{ documentName, documentType, documentId, id, status, documentSubType }]
 *   - documentName: string
 *   - documentType: string (e.g. "ADMISSIONDOCUMENT")
 *   - documentId: string (e.g. "201")
 *   - id: string (same as documentId)
 *   - status: "ACTIVE" | "INACTIVE"
 *   - documentSubType: string (e.g. "Labs")
 */
function updateDocTypeMapping(payload) {
  return axios({
    method: "put",
    url: `${REACT_APP_API_URL}/update-doc-type-mapping`,
    headers: getAuthorizationToken(),
    data: Array.isArray(payload) ? payload : [payload],
  });
}

/**
 * PUT update document–scheme–speciality–caseType mapping
 * Same endpoint, two payload shapes:
 * - Save: [{ schemeTypeMasterId, specialityId, documentTypeMappingId, caseType }]
 * - Delete: [{ id: "<uuid-from-default-ctype-speciality-docs>", status: "INACTIVE" }]
 * @param {Array} payload - Save or delete payload as above
 */
function updateDocTypeCaseTypeSpecialityMapping(payload) {
  const data = Array.isArray(payload) ? payload : [payload];
  return axios({
    method: "put",
    url: `${REACT_APP_API_URL}/update-doctype-casetype-speciality-mapping`,
    headers: getAuthorizationToken(),
    data,
  });
}

/**
 * PUT update document–procedure mapping
 * Endpoint: update-doctype-procedure-mapping?task=mapping
 * @param {Array<{ procedureSchemeId: string|number, documentTypeMappingId: string|number }>} payload
 * @param {string} [task="mapping"] - task query param, default "mapping"
 */
function updateDocTypeProcedureMapping(payload, task = "mapping") {
  const data = Array.isArray(payload) ? payload : [payload];
  return axios({
    method: "put",
    url: `${REACT_APP_API_URL}/update-doctype-procedure-mapping`,
    headers: getAuthorizationToken(),
    params: {
      ...(task != null &&
        String(task).trim() !== "" && { task: String(task).trim() }),
    },
    data,
  });
}

/**
 * GET default docs by caseType, scheme, documentType, speciality
 * @param {Object} params - { caseType, schemeTypeMasterId, documentType, specialityId }
 * documentType e.g. ADMISSIONDOCUMENT, DURINGTREATMENTDOCUMENT, ONDISCHARGEDOCUMENT
 */
function getDefaultCtypeSpecialityDocs(params = {}) {
  const { caseType, schemeTypeMasterId, documentType, specialityId } = params;
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/default-ctype-speciality-docs`,
    headers: getAuthorizationToken(),
    params: {
      ...(caseType != null && String(caseType).trim() !== "" && { caseType: String(caseType).trim() }),
      ...(schemeTypeMasterId != null && String(schemeTypeMasterId).trim() !== "" && { schemeTypeMasterId: String(schemeTypeMasterId).trim() }),
      ...(documentType != null && String(documentType).trim() !== "" && { documentType: String(documentType).trim() }),
      ...(specialityId != null && String(specialityId).trim() !== "" && { specialityId: String(specialityId).trim() }),
    },
  });
}


/**
 * GET default procedure mapped documents by procedureSchemeIds (path) and documentType (query).
 * procedureSchemeIds: array of ids from scheme-procedure-list; sent as URL-encoded JSON in path.
 * @param {string[]} procedureSchemeIds - e.g. ["ac64dbdc-e069-48b4-a67f-d3ffbc2f3a12"]
 * @param {string} documentType - e.g. ADMISSIONDOCUMENT, DURINGTREATMENTDOCUMENT, ONDISCHARGEDOCUMENT
 */
function getDefaultProcedureMappedDocuments(procedureSchemeIds, documentType) {
  const ids = Array.isArray(procedureSchemeIds) ? procedureSchemeIds : [];
  const encoded = encodeURIComponent(JSON.stringify(ids));
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/default-procedure-mapped-documents/${encoded}`,
    headers: getAuthorizationToken(),
    params: {
      ...(documentType != null &&
        String(documentType).trim() !== "" && {
          documentType: String(documentType).trim(),
        }),
    },
  });
}

/**
 * PUT add/update procedure–procedure mapping (parent with multiple children).
 * @param {Array<{ parentProcedureSchemeId: string, childProcedureSchemeId: string }>} payload - one entry per parent–child pair. Response returns same shape.
 */
function addUpdateProcedureProcedureMapping(payload) {
  const data = Array.isArray(payload) ? payload : [payload];
  return axios({
    method: "put",
    url: `${REACT_APP_API_URL}/add-update-procedure-procedure-mapping`,
    headers: getAuthorizationToken(),
    data,
  });
}

/**
 * GET child procedure list for a parent procedure (mapped children).
 * @param {Object} params - { procedureSchemeId: string (parent procedure-scheme id), page?, limit? }
 * @returns {Promise} - { data: { mappedProcdures: Array, totalPages, total } } (note: API typo "mappedProcdures")
 */
function getChildProcedureList(params = {}) {
  return axios({
    method: "get",
    url: `${REACT_APP_API_URL}/child-procedure-list`,
    headers: getAuthorizationToken(),
    params: {
      ...(params.procedureSchemeId != null &&
        params.procedureSchemeId !== "" && {
          procedureSchemeId: params.procedureSchemeId,
        }),
      ...(params.page != null && { page: params.page }),
      ...(params.limit != null && { limit: params.limit }),
    },
  });
}

export const ProcedureFlowServices = {
  getDefaultSchemeTypes,
  addSchemeType,
  updateSchemeType,
  updateSchemeStateMapping,
  getSchemeProcedureCostList,
  updateProcedureCost,
  addProcedureCostMapping,
  mapProcedureSchemeCost,
  getMasterDataList,
  addProcedure,
  getSpecialityTypes,
  addDefaultSpeciality,
  searchProcedureById,
  getDefaultProcedureListV2,
  updateDefaultProcedureSpeciality,
  getProcedures,
  getSchemeProcedureList,
  updateProcedureSchemeMapping,
  getDefaultDocumentNameList,
  getDefaultDocumentNameListV2,
  getGroupedDefaultDocumentNameListV2,
  getDocumentLinkedProcedures,
  addUpdateDefaultDocName,
  updateDocTypeMapping,
  updateDocTypeCaseTypeSpecialityMapping,
  updateDocTypeProcedureMapping,
  getDefaultCtypeSpecialityDocs,
  getDefaultProcedureMappedDocuments,
  getProcedureslist,
  addUpdateProcedureProcedureMapping,
  getChildProcedureList,
};
